/* istanbul ignore file */
import { useMemo, useCallback } from "react";
import { useSelector } from "@/core/redux/store";
import { SpecificationItemAnswerStatus } from "@/shared/enums/specification-item-answer";
import {
  ISurveyAnswer,
  ISupplierCategory,
  ISupplierCategoryItem,
} from "@/shared/services/interface/responses/suplier-response";
import { ISpecificationCategoryOrItemResponse } from "@/shared/services/interface/responses/ISpecificationResponse";

const findSurveyAnswerForItem = (
  categories: ISupplierCategory[] | undefined,
  itemId: string
): ISurveyAnswer | null => {
  if (!categories) return null;
  for (const category of categories) {
    for (const child of category.children) {
      if (child.type === "item" && child.id === itemId) {
        return (child as ISupplierCategoryItem).surveyAnswer;
      }
      if (child.type === "category") {
        const nested = findSurveyAnswerForItem(
          [child as ISupplierCategory],
          itemId
        );
        if (nested !== null) return nested;
      }
    }
  }
  return null;
};

/**
 * Hook that provides a filter function for sidebar items based on the current sidebarFilter state.
 * Returns `shouldShowItem(itemId)` that checks if an item passes the active filters.
 */
export function useSidebarItemFilter() {
  const sidebarFilter = useSelector(
    (state) => state.specification.sidebarFilter
  );
  const supplierCategoriesResponse = useSelector(
    (state) => state.productAnswer.supplierCategoriesResponse
  );
  const supplierSurveys = useSelector(
    (state) =>
      state.productAnswer.supplierAndAIAnswersResponse?.supplierSurveys
  );
  const aiAnswers = useSelector(
    (state) =>
      state.productAnswer.supplierAndAIAnswersResponse
        ?.specificationItemAnswers
  );
  const transientSelectedAnswer = useSelector(
    (state) => state.productAnswer.transientSelectedAnswer
  );

  const hasActiveFilter =
    sidebarFilter.answerStatuses.length > 0 || sidebarFilter.showConflicts;

  const shouldShowItem = useCallback(
    (itemId: string): boolean => {
      if (!hasActiveFilter) return true;

      // Get survey answer
      let surveyAnswer: ISurveyAnswer | null = null;
      if (transientSelectedAnswer?.specificationItemId === itemId) {
        surveyAnswer = {
          status: transientSelectedAnswer.status,
          description: "",
        };
      } else {
        const fromEndpoint = supplierSurveys?.find(
          (s) => s.specificationItemId === itemId
        );
        if (fromEndpoint) {
          surveyAnswer = {
            status: fromEndpoint.status,
            description: fromEndpoint.description ?? "",
          };
        } else {
          surveyAnswer = findSurveyAnswerForItem(
            supplierCategoriesResponse?.categories,
            itemId
          );
        }
      }

      // Check conflict filter
      if (sidebarFilter.showConflicts) {
        if (surveyAnswer && aiAnswers) {
          const aiAnswer = aiAnswers.find(
            (a) => a.specificationItemId === itemId
          );
          if (aiAnswer) {
            const answered = [
              SpecificationItemAnswerStatus.ANSWERED_YES,
              SpecificationItemAnswerStatus.ANSWERED_NO,
              SpecificationItemAnswerStatus.ANSWERED_PARTIALLY,
            ];
            if (
              answered.includes(surveyAnswer.status) &&
              answered.includes(
                aiAnswer.status as SpecificationItemAnswerStatus
              ) &&
              surveyAnswer.status !== aiAnswer.status
            ) {
              return true; // Item has conflict, show it
            }
          }
        }
      }

      // Check answer status filters
      if (sidebarFilter.answerStatuses.length > 0) {
        const status = surveyAnswer?.status;
        if (status !== undefined && status !== null) {
          return sidebarFilter.answerStatuses.includes(status);
        }
        // Item has no answer — only show if SEARCHING (0) is in filters
        return sidebarFilter.answerStatuses.includes(
          SpecificationItemAnswerStatus.SEARCHING
        );
      }

      return false;
    },
    [
      hasActiveFilter,
      sidebarFilter.answerStatuses,
      sidebarFilter.showConflicts,
      supplierCategoriesResponse,
      supplierSurveys,
      aiAnswers,
      transientSelectedAnswer,
    ]
  );

  /**
   * Check if a category (with its children) has any visible items after filtering.
   */
  const hasVisibleChildren = useCallback(
    (children: ISpecificationCategoryOrItemResponse[] | null): boolean => {
      if (!hasActiveFilter) return true;
      if (!children) return false;

      for (const child of children) {
        if (child.type === "item") {
          if (shouldShowItem(child.id)) return true;
        } else if (child.type === "category") {
          const cat = child as any;
          if (hasVisibleChildren(cat.children)) return true;
        }
      }
      return false;
    },
    [hasActiveFilter, shouldShowItem]
  );

  return { shouldShowItem, hasVisibleChildren, hasActiveFilter };
}
